import React, { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import Skeleton from '@mui/material/Skeleton'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Add from '@mui/icons-material/Add'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import DriveFileRenameOutline from '@mui/icons-material/DriveFileRenameOutline'
import Tune from '@mui/icons-material/Tune'
import { TOUCH_MIN } from 'core/theme.tokens'
import { formatAssistantChatTime, type AssistantChatSummary } from './assistantChats'
import type { AssistantProfileSummary } from './assistantProfiles'

interface AssistantHistoryPaneProps {
  tab: 'chats' | 'assistants'
  onTab: (tab: 'chats' | 'assistants') => void
  threads: AssistantChatSummary[]
  activeId: string | null
  streamingIds?: string[]
  loading?: boolean
  profiles: AssistantProfileSummary[]
  profilesLoading?: boolean
  profilesError?: string | null
  activeProfileId: string | null
  onNew: () => void
  onOpen: (id: string) => void
  onRename: (id: string, title: string) => void
  onDelete: (id: string) => void
  onNewAssistant: () => void
  onOpenAssistant: (id: string) => void
  onEditAssistant: (id: string) => void
}

export const AssistantHistoryPane: React.FC<AssistantHistoryPaneProps> = ({
  tab,
  onTab,
  threads,
  activeId,
  streamingIds = [],
  loading = false,
  profiles,
  profilesLoading = false,
  profilesError = null,
  activeProfileId,
  onNew,
  onOpen,
  onRename,
  onDelete,
  onNewAssistant,
  onOpenAssistant,
  onEditAssistant,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const skipCommitRef = useRef(false)

  const startRename = (thread: AssistantChatSummary) => {
    skipCommitRef.current = false
    setEditingId(thread.id)
    setDraft(thread.title)
  }

  const cancelRename = () => {
    skipCommitRef.current = true
    setEditingId(null)
  }

  const commitRename = (thread: AssistantChatSummary) => {
    if (skipCommitRef.current) {
      skipCommitRef.current = false
      return
    }
    const next = draft.replace(/\s+/g, ' ').trim()
    setEditingId(null)
    if (!next || next === thread.title) return
    onRename(thread.id, next)
  }

  return (
    <Box
      sx={{
        width: { xs: '100%', sm: 280 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        borderRight: { sm: 1 },
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Tabs
        value={tab}
        onChange={(_, value: 'chats' | 'assistants') => onTab(value)}
        variant='fullWidth'
        sx={{ minHeight: 36, flexShrink: 0 }}
      >
        <Tab value='chats' label='Chats' sx={{ minHeight: 36, py: 0.5, textTransform: 'none' }} />
        <Tab
          value='assistants'
          label='Assistants'
          sx={{ minHeight: 36, py: 0.5, textTransform: 'none' }}
        />
      </Tabs>
      <Box sx={{ px: 1, py: 0.75, flexShrink: 0 }}>
        <Button
          size='small'
          variant='outlined'
          startIcon={<Add />}
          onClick={tab === 'chats' ? onNew : onNewAssistant}
          disabled={tab === 'chats' ? loading : profilesLoading}
          sx={{
            minHeight: 32,
            px: 1.25,
            textTransform: 'none',
            fontSize: '0.8125rem',
          }}
        >
          {tab === 'chats' ? 'New chat' : 'New assistant'}
        </Button>
      </Box>
      <List sx={{ flex: 1, overflowY: 'auto', py: 0, px: 0.5 }}>
        {tab === 'assistants' ? (
          profilesError ? (
            <Typography
              variant='caption'
              color='error'
              sx={{ px: 1.5, py: 1, display: 'block', fontSize: '0.8125rem' }}
            >
              {profilesError}
            </Typography>
          ) : profilesLoading ? (
            <Box sx={{ px: 1, py: 0.5 }}>
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton
                  key={`profile-skel-${index}`}
                  variant='rounded'
                  height={TOUCH_MIN - 8}
                  sx={{ mb: 0.75, borderRadius: 1 }}
                />
              ))}
            </Box>
          ) : profiles.length === 0 ? (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ px: 1.5, py: 1, display: 'block', fontSize: '0.8125rem' }}
            >
              No assistants yet
            </Typography>
          ) : (
            profiles.map(profile => (
              <ListItem key={profile.id} disablePadding sx={{ mb: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.25 }}>
                  <ListItemButton
                    selected={profile.id === activeProfileId}
                    onClick={() => onOpenAssistant(profile.id)}
                    sx={{ flex: 1, minWidth: 0, borderRadius: 1, minHeight: 44, py: 0.5 }}
                  >
                    <Typography noWrap variant='body2' sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>
                      {profile.name}
                    </Typography>
                  </ListItemButton>
                  <Tooltip title='Assistant details'>
                    <IconButton
                      size='small'
                      aria-label={`Details for ${profile.name}`}
                      onClick={() => onEditAssistant(profile.id)}
                    >
                      <Tune fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            ))
          )
        ) : null}
        {tab === 'chats' ? (
          loading ? (
          <Box sx={{ px: 1, py: 0.5 }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton
                key={`thread-skel-${index}`}
                variant='rounded'
                height={TOUCH_MIN - 8}
                sx={{ mb: 0.75, borderRadius: 1 }}
              />
            ))}
          </Box>
        ) : threads.length === 0 ? (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ px: 1.5, py: 1, display: 'block', fontSize: '0.8125rem' }}
          >
            No chats yet
          </Typography>
        ) : (
          threads.map(thread => {
            const when = formatAssistantChatTime(thread.updatedAt)
            const editing = editingId === thread.id
            return (
              <ListItem key={thread.id} disablePadding sx={{ mb: 0.25 }}>
                {editing ? (
                  <TextField
                    value={draft}
                    onChange={event => setDraft(event.target.value)}
                    onBlur={() => commitRename(thread)}
                    onKeyDown={event => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                        ;(event.target as HTMLInputElement).blur()
                      }
                      if (event.key === 'Escape') {
                        event.preventDefault()
                        cancelRename()
                      }
                    }}
                    autoFocus
                    fullWidth
                    size='small'
                    placeholder='Chat name'
                    onFocus={event => event.target.select()}
                    inputProps={{
                      maxLength: 80,
                      'aria-label': 'Chat title',
                    }}
                    sx={{
                      mx: 0.5,
                      my: 0.25,
                      '& .MuiInputBase-root': { minHeight: { xs: 40, sm: TOUCH_MIN } },
                      '& .MuiInputBase-input': { fontSize: '0.8125rem', fontWeight: 600 },
                    }}
                  />
                ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.25 }}>
                <ListItemButton
                  selected={thread.id === activeId}
                  onClick={() => onOpen(thread.id)}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: 1,
                    minHeight: { xs: 48, sm: 52 },
                    py: 0.5,
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Tooltip
                      title={thread.title}
                      placement='top'
                      enterDelay={400}
                      slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
                    >
                      <Typography
                        noWrap
                        variant='body2'
                        sx={{ fontWeight: 600, fontSize: '0.8125rem', lineHeight: 1.3 }}
                      >
                        {thread.title}
                      </Typography>
                    </Tooltip>
                    {streamingIds.includes(thread.id) ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <Box
                          aria-hidden
                          sx={{
                            width: 10,
                            height: 10,
                            flexShrink: 0,
                            borderRadius: '50%',
                            border: '1.5px solid',
                            borderColor: 'divider',
                            borderTopColor: 'primary.main',
                            animation: 'assistant-answering 0.8s linear infinite',
                            '@keyframes assistant-answering': {
                              to: { transform: 'rotate(360deg)' },
                            },
                          }}
                        />
                        <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem', lineHeight: 1.3 }}>
                          Answering
                        </Typography>
                      </Box>
                    ) : null}
                    {when ? (
                      <Typography
                        noWrap
                        variant='caption'
                        component='span'
                        sx={{
                          display: 'block',
                          color: 'text.secondary',
                          fontVariantNumeric: 'tabular-nums',
                          fontSize: '0.7rem',
                          lineHeight: 1.3,
                        }}
                      >
                        {when}
                      </Typography>
                    ) : null}
                  </Box>
                </ListItemButton>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                  <Tooltip title='Rename chat'>
                    <IconButton
                      size='small'
                      aria-label={`Rename ${thread.title}`}
                      onClick={() => startRename(thread)}
                    >
                      <DriveFileRenameOutline fontSize='small' />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title='Delete chat'>
                    <IconButton
                      size='small'
                      aria-label={`Delete ${thread.title}`}
                      onClick={() => onDelete(thread.id)}
                    >
                      <DeleteOutline fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
                </Box>
                )}
              </ListItem>
            )
          })
        )
        ) : null}
      </List>
    </Box>
  )
}
